import { useState, useEffect, useCallback, useRef } from 'react';
import { useAPI } from '../contexts/APIContext';
import { resolveStorySubtitles, fetchStorySubtitleUrlsFromApi } from '../utils/storySubtitles';
import logger from '../utils/logger';
import { subtitleDebug } from '../utils/subtitleDebug';

export { SUBTITLE_LANGUAGE_CODES } from '../constants/subtitleLanguages';

const cleanSubtitleUrl = (url) => (url ? String(url).replace(/\?$/, '') : null);

/**
 * Load existing subtitles and request on-demand generation for a language.
 */
export function useStorySubtitles(storyId, story = null) {
  const { apiClient } = useAPI();
  const [subtitleUrls, setSubtitleUrls] = useState({});
  const [loadingLang, setLoadingLang] = useState(null);
  const [error, setError] = useState(null);

  const contentId = storyId || story?.id;
  const subtitleUrlsRef = useRef({});
  const inFlightRef = useRef(new Set());
  const apiStatsRef = useRef({ loadCount: 0, generateCount: 0 });
  const [apiStats, setApiStats] = useState({ loadCount: 0, generateCount: 0 });

  const bumpApiStat = useCallback((key) => {
    apiStatsRef.current[key] += 1;
    setApiStats({ ...apiStatsRef.current });
    subtitleDebug('useStorySubtitles', `API ${key}`, apiStatsRef.current);
  }, []);

  useEffect(() => {
    subtitleUrlsRef.current = subtitleUrls;
  }, [subtitleUrls]);

  useEffect(() => {
    subtitleDebug('useStorySubtitles', 'hook inputs', {
      storyId,
      contentId,
      storySubtitleUrl: story?.subtitle_url,
      storySubtitleUrls: story?.subtitle_urls,
      cachedUrls: subtitleUrls,
    });
  }, [storyId, contentId, story?.subtitle_url, story?.subtitle_urls, subtitleUrls]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const source = story || (contentId ? { id: contentId } : null);
      if (!source?.id && !source?.subtitle_url && !source?.subtitle_urls) {
        subtitleDebug('useStorySubtitles', 'skip load — no source data', { contentId });
        setSubtitleUrls({});
        return;
      }

      subtitleDebug('useStorySubtitles', 'resolveStorySubtitles starting', {
        contentId: source?.id,
        hasInlineUrl: Boolean(source?.subtitle_url),
        hasInlineUrls: Boolean(source?.subtitle_urls),
      });
      bumpApiStat('loadCount');

      const urls = await resolveStorySubtitles(source, apiClient);
      if (!cancelled) {
        setSubtitleUrls(urls);
        subtitleDebug('useStorySubtitles', 'resolveStorySubtitles complete', {
          contentId: source?.id,
          langs: Object.keys(urls),
          urls,
        });
      } else {
        subtitleDebug('useStorySubtitles', 'load cancelled (stale)', { contentId: source?.id });
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [apiClient, contentId, story?.id, story?.subtitle_url, story?.subtitle_urls, bumpApiStat]);

  const refreshSubtitles = useCallback(async () => {
    if (!apiClient || !contentId) return {};
    subtitleDebug('useStorySubtitles', 'refreshSubtitles (manual refetch)', { contentId });
    bumpApiStat('loadCount');
    const urls = await fetchStorySubtitleUrlsFromApi(apiClient, contentId);
    setSubtitleUrls(urls);
    subtitleDebug('useStorySubtitles', 'refreshSubtitles result', {
      contentId,
      langs: Object.keys(urls),
      urls,
    });
    return urls;
  }, [apiClient, contentId, bumpApiStat]);

  const requestSubtitle = useCallback(
    async (languageCode = 'en') => {
      const lang = (languageCode || 'en').toLowerCase();
      if (!contentId || !apiClient) {
        subtitleDebug('useStorySubtitles', 'requestSubtitle skipped — no contentId/apiClient', {
          lang,
          contentId,
        });
        return null;
      }

      subtitleDebug('useStorySubtitles', 'requestSubtitle called', {
        lang,
        contentId,
        cached: Boolean(subtitleUrlsRef.current[lang]),
        inFlight: inFlightRef.current.has(lang),
      });

      if (subtitleUrlsRef.current[lang]) {
        subtitleDebug('useStorySubtitles', 'cache hit — skip generate API', {
          lang,
          url: subtitleUrlsRef.current[lang],
        });
        return subtitleUrlsRef.current[lang];
      }

      if (inFlightRef.current.has(lang)) {
        subtitleDebug('useStorySubtitles', 'skip duplicate in-flight generate', { lang });
        return null;
      }

      inFlightRef.current.add(lang);
      setLoadingLang(lang);
      setError(null);

      subtitleDebug('useStorySubtitles', 'POST /generate-subtitles starting', { lang, contentId });
      bumpApiStat('generateCount');

      try {
        logger.log('[subtitles] Generating subtitle', { storyId: contentId, lang });
        const response = await apiClient.generateStorySubtitles(contentId, lang);
        const url = cleanSubtitleUrl(response?.data?.subtitle_url);

        subtitleDebug('useStorySubtitles', 'POST /generate-subtitles response', {
          lang,
          status: response?.status,
          url,
          data: response?.data,
        });

        if (!response?.status || !url) {
          throw new Error(response?.error || response?.detail || 'Failed to generate subtitles');
        }

        setSubtitleUrls((prev) => {
          const next = { ...prev, [lang]: url };
          subtitleDebug('useStorySubtitles', 'subtitleUrls updated after generate', {
            lang,
            url,
            allLangs: Object.keys(next),
          });
          return next;
        });
        logger.log('[subtitles] Subtitle ready', { storyId: contentId, lang, url });
        return url;
      } catch (err) {
        const message = err?.message || 'Failed to generate subtitles';
        logger.warn('[subtitles] Generation failed', { storyId: contentId, lang, message });
        subtitleDebug('useStorySubtitles', 'generate failed', { lang, message });
        setError(message);
        return null;
      } finally {
        inFlightRef.current.delete(lang);
        setLoadingLang(null);
      }
    },
    [apiClient, contentId, bumpApiStat]
  );

  return {
    subtitleUrls,
    requestSubtitle,
    refreshSubtitles,
    loadingLang,
    error,
    contentId,
    apiStats,
  };
}

export default useStorySubtitles;
